import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ChatMessage } from '@/types'

interface ChatState {
  // Persisted
  visitorId: string
  sessionId: string | null
  visitorName: string | null
  visitorEmail: string | null

  // Runtime
  isOpen: boolean
  messages: ChatMessage[]
  unreadCount: number
  isOnline: boolean

  // Actions
  setOpen: (open: boolean) => void
  setSession: (sessionId: string) => void
  appendMessage: (message: ChatMessage) => void
  setMessages: (messages: ChatMessage[]) => void
  markAllRead: () => void
  incrementUnread: () => void
  setOnline: (online: boolean) => void
  setVisitorInfo: (name: string, email?: string) => void
  reset: () => void
}

function generateUUID(): string {
  return crypto.randomUUID()
}

export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      visitorId: generateUUID(),
      sessionId: null,
      visitorName: null,
      visitorEmail: null,
      isOpen: false,
      messages: [],
      unreadCount: 0,
      isOnline: false,

      setOpen: (open) => set({ isOpen: open }),

      setSession: (sessionId) => set({ sessionId }),

      appendMessage: (message) =>
        set((state) => ({
          messages: [...state.messages, message],
        })),

      setMessages: (messages) => set({ messages }),

      markAllRead: () => set({ unreadCount: 0 }),

      incrementUnread: () =>
        set((state) => ({ unreadCount: state.unreadCount + 1 })),

      setOnline: (online) => set({ isOnline: online }),

      setVisitorInfo: (name, email) =>
        set({ visitorName: name, visitorEmail: email || null }),

      reset: () =>
        set({
          sessionId: null,
          messages: [],
          unreadCount: 0,
          isOpen: false,
        }),
    }),
    {
      name: 'engitronix-chat-session',
      partialize: (state) => ({
        visitorId: state.visitorId,
        sessionId: state.sessionId,
        visitorName: state.visitorName,
        visitorEmail: state.visitorEmail,
      }),
    }
  )
)
